/**
 * Functionals (analytic components) API calls.
 *
 * Functionals are reusable analytic building blocks (Java classes in JARs)
 * that can be placed on the model canvas and wired together.
 */
import apiClient from './client';
import { buildAuditSearchUrl, type AuditSearchParams } from './auditSearch';
import type {
  Functional,
  FunctionalSearchParams,
  JarFilesResponse,
  SearchParams,
  SkipLimitSearchResponse,
} from '@/types';

// ── Functional audits ─────────────────────────────────────────────────

export type FunctionalAuditType =
  | 'SEARCH'
  | 'EXECUTE'
  | 'CREATE'
  | 'READ'
  | 'UPDATE'
  | 'DELETE';

/** Common attributes plus the verb-specific extras (SEARCH adds
 *  `text`/`count`/`limit`/`skip`; EXECUTE adds `jobId`/`aclType`/`modelId`
 *  plus a `startTime`/`endTime` pair used to derive a duration). */
export interface FunctionalAuditAttributes {
  dn?: string;
  // SEARCH
  text?: string;
  count?: number;
  limit?: number;
  skip?: number;
  // EXECUTE
  jobId?: string;
  aclType?: string;
  modelId?: string;
  startTime?: string;
  endTime?: string;
  /** Server stamps `"true"`/`"false"` as a string when a functional run fails. */
  hasError?: string;
  error?: string;
  [k: string]: unknown;
}

export interface FunctionalAuditEntry {
  subject?: string;
  verb?: FunctionalAuditType;
  attributes?: FunctionalAuditAttributes;
  timestamp?: string;
  /** Either a specific functional URN (`urn:brew:functionals:<uuid>`) or the
   *  collection root `urn:brew:functionals` for SEARCH events. */
  object?: string;
}

/**
 * Search the functional audit log.
 *
 *   GET api/functionals/audits/search?^auditType=EXECUTE&text=*&limit=20&skip=0&sort=-timestamp
 *
 * Same URL-builder pattern as searchModelAudits / searchProfileAudits so the
 * `^auditType` predicate prefix stays unencoded. Omit `auditType` to fetch
 * every verb at once.
 */
export async function searchFunctionalAudits(
  params: AuditSearchParams<FunctionalAuditType> = {},
): Promise<SkipLimitSearchResponse<FunctionalAuditEntry>> {
  const url = buildAuditSearchUrl('api/functionals/audits/search', params);
  const res = await apiClient.get<SkipLimitSearchResponse<FunctionalAuditEntry>>(url);
  return res.data;
}

/** List functionals. API returns a flat array, not paginated. */
export async function listFunctionals(params?: SearchParams): Promise<Functional[]> {
  const res = await apiClient.get<Functional[]>('api/functionals', { params });
  return res.data;
}

/** Get a specific functional. */
export async function getFunctional(id: string): Promise<Functional> {
  const res = await apiClient.get<Functional>(`api/functionals/${id}`);
  return res.data;
}

// ── Ad-hoc functional execution ───────────────────────────────────────

export interface ExecOutput {
  type?: string;
  value?: unknown;
  schema?: { name: string; type: string }[];
  totalRows?: number;
  fetchUrl?: string;
}
export interface ExecResponse {
  executionId?: string;
  status: string;
  durationMs?: number;
  outputs?: Record<string, ExecOutput>;
  warnings?: string[];
  error?: { message?: string; component?: string; trace?: string[] };
}
export interface ExecuteFunctionalArgs {
  inputs?: Record<string, unknown>;
  /** field name → CSV File for table inputs (presence triggers multipart). */
  tableFiles?: Record<string, File>;
  outputs?: string[];
  options?: { timeoutMs?: number };
}

/**
 * Run a functional ad-hoc via the director. Uses multipart/form-data when CSV
 * table files are present (per the Phase 2 contract), JSON otherwise.
 */
export async function executeFunctional(
  id: string,
  args: ExecuteFunctionalArgs,
): Promise<ExecResponse> {
  // `id` reaches here from a brew-ai tool proposal (an LLM-supplied value), so
  // validate it as a functional id before putting it in the path — a value with
  // `/`, `.`, or `\` could otherwise traverse to a different director endpoint.
  if (!/^[a-zA-Z0-9_-]{1,128}$/.test(id)) {
    throw new Error(`Invalid functional id: ${id}`);
  }
  const path = `api/director/functionals/${encodeURIComponent(id)}/execute`;
  const fileEntries = Object.entries(args.tableFiles ?? {});
  if (fileEntries.length === 0) {
    const body: Record<string, unknown> = {};
    if (args.inputs) body.inputs = args.inputs;
    if (args.outputs) body.outputs = args.outputs;
    if (args.options) body.options = args.options;
    const res = await apiClient.post<ExecResponse>(path, body);
    return res.data;
  }
  // Scalars as text fields; non-strings JSON-encoded (an array becomes a JSON
  // array string, which the endpoint's MultipartFieldInterpreter parses to a list).
  const form = new FormData();
  for (const [k, v] of Object.entries(args.inputs ?? {})) {
    form.append(k, typeof v === 'string' ? v : JSON.stringify(v));
  }
  for (const [field, file] of fileEntries) form.append(field, file, file.name);
  if (args.outputs) form.append('outputs', JSON.stringify(args.outputs));
  if (args.options) form.append('options', JSON.stringify(args.options));
  const res = await apiClient.post<ExecResponse>(path, form);
  return res.data;
}

/** Get functional count. */
export async function getFunctionalCount(): Promise<number> {
  const res = await apiClient.get<number | string>('api/functionals/count');
  return Number(res.data) || 0;
}

/**
 * Search functionals. API uses ?text= param and returns a flat array.
 *
 * `lifecycleState` / `excludeLifecycleState` filter by component lifecycle state
 * (TESTING / PRODUCTION / RETIRED). Array values are sent comma-joined to match
 * the backend's comma-split parsing; omit both for the default behavior.
 */
export async function searchFunctionals(
  params?: FunctionalSearchParams
): Promise<Functional[]> {
  const res = await apiClient.get<Functional[]>('api/functionals/search', {
    params: serializeLifecycleParams(params),
  });
  return res.data;
}

/** Collapse array-valued lifecycle filters to comma-joined strings; other
 *  params pass through unchanged. */
function serializeLifecycleParams(
  params?: FunctionalSearchParams
): Record<string, unknown> | undefined {
  if (!params) return params;

  const { lifecycleState, excludeLifecycleState, ...rest } = params;
  const out: Record<string, unknown> = { ...rest };

  if (lifecycleState !== undefined) {
    out.lifecycleState = Array.isArray(lifecycleState)
      ? lifecycleState.join(',')
      : lifecycleState;
  }

  if (excludeLifecycleState !== undefined) {
    out.excludeLifecycleState = Array.isArray(excludeLifecycleState)
      ? excludeLifecycleState.join(',')
      : excludeLifecycleState;
  }

  return out;
}

/** Get functional tags. */
export async function getFunctionalTags(): Promise<string[]> {
  const res = await apiClient.get<string[]>('api/functionals/tags');
  return res.data;
}

/** Get JAR files. API returns { jars: [...], states: {...} }. */
export async function getJarFiles(): Promise<JarFilesResponse> {
  const res = await apiClient.get<JarFilesResponse>('api/functionals/jars');
  return res.data;
}

/** Delete a published JAR and all its related components/resources. */
export async function deleteJar(id: string): Promise<void> {
  await apiClient.delete(`api/libraries/${id}`);
}

/** Get functional audits concordance. */
export async function getFunctionalAudits(): Promise<unknown> {
  const res = await apiClient.get('api/functionals/audits/concordance');
  return res.data;
}

/** Response shape for /functionals/:id/usages and /functionals/:id/users */
export interface FunctionalUsageResponse<T = unknown> {
  total: number;
  items: T[];
}

/** Models that use a given functional. */
export async function getFunctionalUsages(id: string, limit = 10): Promise<FunctionalUsageResponse> {
  const res = await apiClient.get<FunctionalUsageResponse>(
    `/api/functionals/${id}/usages`,
    { params: { limit } },
  );
  return res.data;
}

/** Users who have used a given functional. */
export async function getFunctionalUsers(id: string, limit = 10): Promise<FunctionalUsageResponse> {
  const res = await apiClient.get<FunctionalUsageResponse>(
    `/api/functionals/${id}/users`,
    { params: { limit } },
  );
  return res.data;
}

/** Fetch the raw markdown body at a functional's `metadata.helpUrl`. */
export async function getFunctionalHelpDoc(helpUrl: string): Promise<string> {
  const path = helpUrl.replace(/^\/+/, '');
  const res = await apiClient.get<string>(path, { responseType: 'text', transformResponse: (d) => d });
  return res.data;
}
